import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useGridCopyMenu } from './gridCommon';
import { AgGridReact } from 'ag-grid-react';
import { AllCommunityModule, ClientSideRowModelModule, type ColDef, ModuleRegistry } from 'ag-grid-community';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import { callAxiosGet } from '../tmslib/src/util/axiosutils';
import { useMessageState } from '../tmslib/src/context/MessageContext';
import { getMainAxios } from '../utils/util';

ModuleRegistry.registerModules([AllCommunityModule, ClientSideRowModelModule]);

interface KisProductRow {
  id: number;
  d?: string;
  prodId: string;
  prodNm: string;
  market: string;
  grpCode: string;
  stdPrice?: number | null;
  mktamt?: number | null;
  ltdstknum?: number | null;
  bfDayTrdVol?: number | null;
  facevalue?: number | null;
  mktcapsize?: number | null;
  transactionSuspended?: boolean;
  managementItems?: boolean;
  mktWarning?: number;
  kospi100?: boolean;
  kosdaq150?: boolean;
  krx300?: boolean;
  updt?: string;
}

// 선물 종목 (2026-11-01) — /stocks/master/futures. 지수선물(futidxmst)·주식선물(futstkmst) 의 선물(F)·스프레드(SP)만
interface FutRow {
  d?: string;
  prodId: string;
  prodNm: string;
  market: string;          // '선물' | '주식선물'
  prodTp: string;
  prodTpNm: string;        // 지수선물 · 미니선물 · 코스피 주식선물 ...
  prodCls: string;         // F | SP
  stdCd?: string | null;
  undlyProdId?: string | null;
  undlyProdNm?: string | null;
  undlyMkt?: string | null; // 주식선물 기초자산 시장 (KOSPI/KOSDAQ)
  matMon?: string | null;
  monthCls?: string | null;
  isSpread: boolean;
  isMini: boolean;
  rtSubscribable: boolean;
}
type MarketSel = '' | 'KOSPI' | 'KOSDAQ' | 'FUT' | 'SFUT';
const marketLabel: Record<MarketSel, string> = { '': '전체', KOSPI: 'KOSPI', KOSDAQ: 'KOSDAQ', FUT: '선물', SFUT: '주식선물' };
const isFutSel = (m: MarketSel) => m === 'FUT' || m === 'SFUT';
const monthClsLabel = (v?: string | null) =>
  v == null || v === '' ? '' : v === '0' ? '연결' : v === '1' ? '최근월' : v === '2' ? '차근월' : `${v}번째`;

const futDetailFields: { key: keyof FutRow; label: string }[] = [
  { key: 'prodId', label: '종목코드' }, { key: 'stdCd', label: '표준코드' }, { key: 'prodNm', label: '종목명' },
  { key: 'prodTpNm', label: '상품종류' }, { key: 'prodCls', label: '상품구분(F/SP)' },
  { key: 'undlyProdId', label: '기초자산코드' }, { key: 'undlyProdNm', label: '기초자산명' }, { key: 'undlyMkt', label: '기초자산시장' },
  { key: 'matMon', label: '만기월' }, { key: 'monthCls', label: '월물구분' },
  { key: 'isMini', label: '미니' }, { key: 'rtSubscribable', label: '실시간 구독대상' }, { key: 'd', label: '기준일' },
];

const grpCodeLabels: Record<string, string> = {
  ST: '주권', EF: 'ETF', EN: 'ETN', BC: '수익증권', RT: '리츠',
  MF: '증권투자', SC: '선박투자', IF: 'SOC투자', DR: 'DR', SW: '신주인수',
  SR: '신주인수증', FE: '해외ETF', FS: '외국주권', EW: 'ELW',
};

// 상세 패널 — 필드를 카테고리로 묶어 보여줌
const detailGroups: { title: string; fields: { key: string; label: string; type?: 'num' | 'bool' | 'date' | 'str' }[] }[] = [
  {
    title: '기본정보', fields: [
      { key: 'prodId', label: '종목코드' },
      { key: 'prodNm', label: '종목명' },
      { key: 'stdCd', label: '표준코드' },
      { key: 'grpCode', label: '종목구분' },
      { key: 'd', label: '기준일' },
      { key: 'ltd', label: '상장일자' },
      { key: 'settlementMonth', label: '결산월', type: 'num' },
      { key: 'grpCompCode', label: '그룹사코드' },
    ],
  },
  {
    title: '시세 / 시가총액', fields: [
      { key: 'stdPrice', label: '기준가', type: 'num' },
      { key: 'facevalue', label: '액면가', type: 'num' },
      { key: 'mktamt', label: '시가총액(억)', type: 'num' },
      { key: 'mktcapsize', label: '시가총액규모', type: 'num' },
      { key: 'ltdstknum', label: '상장주수(천)', type: 'num' },
      { key: 'capital', label: '자본금', type: 'num' },
      { key: 'bfDayTrdVol', label: '전일거래량', type: 'num' },
      { key: 'publicOfferingPrice', label: '공모가', type: 'num' },
      { key: 'preferredStock', label: '우선주구분', type: 'num' },
    ],
  },
  {
    title: '재무', fields: [
      { key: 'sales', label: '매출액', type: 'num' },
      { key: 'operatingProfit', label: '영업이익', type: 'num' },
      { key: 'ordinaryProfit', label: '경상이익', type: 'num' },
      { key: 'netIncome', label: '당기순이익', type: 'num' },
      { key: 'roe', label: 'ROE(%)', type: 'num' },
      { key: 'baseYearMonth', label: '기준년월', type: 'num' },
    ],
  },
  {
    title: '거래 상태', fields: [
      { key: 'transactionSuspended', label: '거래정지', type: 'bool' },
      { key: 'clearanceSale', label: '정리매매', type: 'bool' },
      { key: 'managementItems', label: '관리종목', type: 'bool' },
      { key: 'mktWarning', label: '시장경고(0:없,1:주의,2:경고,3:위험)', type: 'num' },
      { key: 'warningNotice', label: '경고예고', type: 'bool' },
      { key: 'disclosureInsincerity', label: '불성실공시', type: 'bool' },
      { key: 'backdoorListing', label: '우회상장', type: 'bool' },
      { key: 'shortSellingOverheated', label: '공매도과열', type: 'bool' },
      { key: 'abnormalSurge', label: '이상급등', type: 'bool' },
      { key: 'shortTermOverheating', label: '단기과열', type: 'num' },
    ],
  },
  {
    title: '신용 / 매매', fields: [
      { key: 'mrgnRt', label: '증거금비율(%)', type: 'num' },
      { key: 'creditAvailable', label: '신용가능', type: 'bool' },
      { key: 'creditPeriod', label: '신용기간', type: 'num' },
      { key: 'trdQtyUnit', label: '매매수량단위', type: 'num' },
      { key: 'overHoursQtyUnit', label: '시간외수량단위', type: 'num' },
      { key: 'collateralLoanAvailable', label: '담보대출가능', type: 'bool' },
      { key: 'lendingPossible', label: '대주가능', type: 'bool' },
      { key: 'companyCreditLimitExceeded', label: '회사신용한도초과', type: 'bool' },
      { key: 'lockClassification', label: '락구분', type: 'num' },
      { key: 'faceChg', label: '액면변경', type: 'num' },
      { key: 'capitalIncreaseClassification', label: '증자구분', type: 'num' },
    ],
  },
  {
    title: '지수 편입', fields: [
      { key: 'kospi', label: 'KOSPI', type: 'bool' },
      { key: 'kospi100', label: 'KOSPI100', type: 'bool' },
      { key: 'kospi50', label: 'KOSPI50', type: 'bool' },
      { key: 'kosdaq150', label: 'KOSDAQ150', type: 'bool' },
      { key: 'krx', label: 'KRX', type: 'bool' },
      { key: 'krx100', label: 'KRX100', type: 'bool' },
      { key: 'krx300', label: 'KRX300', type: 'bool' },
      { key: 'krxCar', label: 'KRX 자동차', type: 'bool' },
      { key: 'krxSemiconductor', label: 'KRX 반도체', type: 'bool' },
      { key: 'krxBio', label: 'KRX 바이오', type: 'bool' },
      { key: 'krxBank', label: 'KRX 은행', type: 'bool' },
      { key: 'krxEnergyChemical', label: 'KRX 에너지화학', type: 'bool' },
      { key: 'krxSteel', label: 'KRX 철강', type: 'bool' },
      { key: 'krxMediaCommunications', label: 'KRX 미디어통신', type: 'bool' },
      { key: 'krxConstruction', label: 'KRX 건설', type: 'bool' },
      { key: 'krxSecurities', label: 'KRX 증권', type: 'bool' },
      { key: 'krxShip', label: 'KRX 선박', type: 'bool' },
      { key: 'krxSectorInsurance', label: 'KRX 보험', type: 'bool' },
      { key: 'krxSectorTransportation', label: 'KRX 운송', type: 'bool' },
      { key: 'sri', label: 'SRI', type: 'bool' },
      { key: 'spac', label: 'SPAC', type: 'bool' },
      { key: 'elwIssuance', label: 'ELW발행', type: 'bool' },
      { key: 'lowLiquidity', label: '저유동성', type: 'bool' },
      { key: 'manufacturing', label: '제조업', type: 'bool' },
      { key: 'governanceIndexItems', label: '지배구조지수', type: 'bool' },
      { key: 'kospi200SectorIndustry', label: 'KOSPI200섹터' },
      { key: 'idxSectorLg', label: '지수대분류', type: 'num' },
      { key: 'idxSectorMid', label: '지수중분류', type: 'num' },
      { key: 'idxSectorSmall', label: '지수소분류', type: 'num' },
      { key: 'ventureCompany', label: '벤처기업', type: 'bool' },
      { key: 'investmentCautionaryItems', label: '투자주의환기', type: 'bool' },
      { key: 'non1', label: 'Non1', type: 'bool' },
      { key: 'etp', label: 'ETP', type: 'num' },
    ],
  },
];

const fmtVal = (v: any, type?: 'num' | 'bool' | 'date' | 'str'): string => {
  if (v == null || v === '') return '-';
  if (type === 'bool') return v ? 'Y' : 'N';
  if (typeof v === 'boolean') return v ? 'Y' : 'N';
  if (typeof v === 'number') return v.toLocaleString();
  return String(v);
};

const StockMaster: React.FC = () => {
  const copyMenu = useGridCopyMenu();
  const [rows, setRows] = useState<KisProductRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [market, setMarket] = useState<MarketSel>('');
  const [futRows, setFutRows] = useState<FutRow[]>([]);
  const [futSpread, setFutSpread] = useState(false);
  const [futDetail, setFutDetail] = useState<FutRow | null>(null);
  const [grpCodes, setGrpCodes] = useState<{ ST: boolean; EF: boolean; EN: boolean }>({ ST: true, EF: true, EN: true });
  // 백엔드 응답: 평탄한 객체 (kospimst/kosdaqmst 모든 필드 + market)
  const [detail, setDetail] = useState<Record<string, any> | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const { msgBox: m, logger } = useMessageState();
  const axiosInstance = getMainAxios();

  const fetchFutures = useCallback((sel: MarketSel, spread: boolean) => {
    setLoading(true);
    setDetail(null); setFutDetail(null);
    const params: Record<string, string> = { kind: sel === 'SFUT' ? 'stk' : 'idx', spread: String(spread) };
    if (search.trim()) params.search = search.trim();
    callAxiosGet({
      axiosInstance, m, logger,
      url: '/api/v1/stocks/master/futures',
      params,
      onSuccess: (data) => { setFutRows((data as FutRow[]) || []); setLoading(false); },
      onError: () => { setFutRows([]); setLoading(false); },
    });
  }, [axiosInstance, m, logger, search]);

  const fetchList = useCallback(() => {
    if (isFutSel(market)) { fetchFutures(market, futSpread); return; }
    setFutDetail(null);
    setLoading(true);
    // kisproduct 는 일자별 누적 테이블(99일 x 4천여종목 ≈ 42만행)이다.
    // latestOnly 를 빼면 전 일자가 내려와 응답이 171MB 가 되고(실측 2026-08-28),
    // 서버는 200 을 주지만 브라우저가 렌더링하지 못해 화면이 빈다.
    // 종목정보 화면은 최신 일자만 필요하므로 항상 최신 1일치만 받는다.
    const params: Record<string, string> = { latestOnly: 'true' };
    if (market) params.market = market;
    if (search.trim()) params.search = search.trim();
    callAxiosGet({
      axiosInstance, m, logger,
      url: '/api/v1/stocks/master/list',
      params,
      onSuccess: (data) => { setRows((data as KisProductRow[]) || []); setLoading(false); },
      onError: () => { setRows([]); setLoading(false); },
    });
  }, [axiosInstance, m, logger, market, search, futSpread, fetchFutures]);

  useEffect(() => { fetchList(); }, []);
  // 선물·주식선물은 고르는 즉시 조회한다 (주식 목록과 응답 형태가 달라 이전 결과를 그대로 둘 수 없다)
  useEffect(() => { if (isFutSel(market)) fetchFutures(market, futSpread); }, [market, futSpread]);

  const fetchDetail = (prodId: string) => {
    setDetailLoading(true);
    callAxiosGet({
      axiosInstance, m, logger,
      url: `/api/v1/stocks/master/${prodId}`,
      params: {},
      onSuccess: (data) => { setDetail((data as Record<string, any>) || null); setDetailLoading(false); },
      onError: () => { setDetail(null); setDetailLoading(false); },
    });
  };

  const filteredRows = useMemo(() => {
    const enabled = Object.entries(grpCodes).filter(([, v]) => v).map(([k]) => k);
    if (enabled.length === 3) return rows;
    return rows.filter(r => enabled.includes(r.grpCode));
  }, [rows, grpCodes]);

  const stats = useMemo(() => {
    const total = filteredRows.length;
    const kospi = filteredRows.filter(r => r.market === 'KOSPI').length;
    const kosdaq = filteredRows.filter(r => r.market === 'KOSDAQ').length;
    const suspended = filteredRows.filter(r => r.transactionSuspended).length;
    return { total, kospi, kosdaq, suspended };
  }, [filteredRows]);

  const futStats = useMemo(() => {
    const byTp = new Map<string, number>();
    futRows.forEach(r => byTp.set(r.prodTpNm, (byTp.get(r.prodTpNm) ?? 0) + 1));
    return { total: futRows.length, byTp: [...byTp.entries()].sort((a, b) => b[1] - a[1]).slice(0, 4) };
  }, [futRows]);

  const futColumnDefs = useMemo<ColDef<FutRow>[]>(() => [
    { field: 'prodId', headerName: '종목코드', width: 100, pinned: 'left', cellStyle: { fontFamily: 'monospace', fontWeight: 500 } },
    { field: 'prodNm', headerName: '종목명', width: 230, pinned: 'left', filter: 'agTextColumnFilter' },
    {
      field: 'market', headerName: '구분', width: 85,
      cellStyle: (p: any) => ({ textAlign: 'center', fontWeight: 600, color: p.value === '선물' ? '#b45309' : '#047857' }),
    },
    { field: 'prodTpNm', headerName: '상품종류', width: 130, cellStyle: { textAlign: 'center' } },
    {
      field: 'prodCls', headerName: '선물/SP', width: 80,
      valueFormatter: p => p.value === 'SP' ? '스프레드' : '선물',
      cellStyle: (p: any) => ({ textAlign: 'center', color: p.value === 'SP' ? '#6b7280' : '#1e40af' }),
    },
    { field: 'undlyProdNm', headerName: '기초자산', width: 140, filter: 'agTextColumnFilter' },
    { field: 'undlyProdId', headerName: '기초코드', width: 90, cellStyle: { fontFamily: 'monospace', textAlign: 'center' } },
    { field: 'undlyMkt', headerName: '기초시장', width: 85, cellStyle: { textAlign: 'center' } },
    {
      field: 'matMon', headerName: '만기월', width: 85, sort: 'asc',
      valueFormatter: p => p.value && p.value.length === 6 ? `${p.value.slice(0, 4)}-${p.value.slice(4)}` : (p.value ?? ''),
      cellStyle: { textAlign: 'center' },
    },
    { field: 'monthCls', headerName: '월물', width: 80, valueFormatter: p => monthClsLabel(p.value), cellStyle: { textAlign: 'center' } },
    {
      field: 'rtSubscribable', headerName: '실시간', width: 75,
      cellRenderer: (p: any) => p.value ? <span style={{ color: '#16a34a', fontWeight: 600 }}>Y</span> : '',
      cellStyle: { textAlign: 'center' },
    },
    { field: 'stdCd', headerName: '표준코드', width: 130, cellStyle: { fontFamily: 'monospace' } },
  ], []);
  const futSel = isFutSel(market);

  const columnDefs = useMemo<ColDef<KisProductRow>[]>(() => [
    { field: 'prodId', headerName: '종목코드', width: 100, pinned: 'left', cellStyle: { fontFamily: 'monospace', fontWeight: 500 } },
    { field: 'prodNm', headerName: '종목명', width: 200, pinned: 'left', filter: 'agTextColumnFilter' },
    {
      field: 'market', headerName: '시장', width: 80,
      cellStyle: (p: any) => ({ textAlign: 'center', color: p.value === 'KOSPI' ? '#1d4ed8' : '#7c3aed', fontWeight: 600 }),
    },
    {
      field: 'grpCode', headerName: '종류', width: 80,
      valueFormatter: p => grpCodeLabels[p.value] || p.value,
      cellStyle: { textAlign: 'center' },
    },
    {
      field: 'stdPrice', headerName: '기준가', width: 100, type: 'numericColumn',
      valueFormatter: p => p.value != null ? p.value.toLocaleString() : '',
    },
    {
      field: 'mktamt', headerName: '시가총액(억)', width: 130, type: 'numericColumn',
      valueFormatter: p => p.value != null ? p.value.toLocaleString() : '',
    },
    {
      field: 'ltdstknum', headerName: '상장주수(천)', width: 130, type: 'numericColumn',
      valueFormatter: p => p.value != null ? p.value.toLocaleString() : '',
    },
    {
      field: 'bfDayTrdVol', headerName: '전일거래량', width: 130, type: 'numericColumn',
      valueFormatter: p => p.value != null ? p.value.toLocaleString() : '',
    },
    {
      field: 'facevalue', headerName: '액면가', width: 90, type: 'numericColumn',
      valueFormatter: p => p.value != null ? p.value.toLocaleString() : '',
    },
    {
      field: 'transactionSuspended', headerName: '거래정지', width: 90,
      cellRenderer: (p: any) => p.value ? <span style={{ color: '#dc2626', fontWeight: 600 }}>Y</span> : '',
      cellStyle: { textAlign: 'center' },
    },
    {
      field: 'managementItems', headerName: '관리종목', width: 90,
      cellRenderer: (p: any) => p.value ? <span style={{ color: '#ea580c', fontWeight: 600 }}>Y</span> : '',
      cellStyle: { textAlign: 'center' },
    },
    {
      field: 'mktWarning', headerName: '시장경고', width: 90,
      valueFormatter: p => ({ 0: '', 1: '주의', 2: '경고', 3: '위험' }[p.value as number] ?? ''),
      cellStyle: (p: any) => ({
        textAlign: 'center',
        color: p.value === 3 ? '#dc2626' : p.value === 2 ? '#ea580c' : p.value === 1 ? '#ca8a04' : '#1e40af',
        fontWeight: p.value > 0 ? 600 : 400,
      }),
    },
  ], []);

  const labelStyle: React.CSSProperties = { fontWeight: 'bold', color: '#1e40af', fontSize: '12px', whiteSpace: 'nowrap' };
  const inputStyle: React.CSSProperties = { padding: '6px 10px', border: '2px solid #3b82f6', borderRadius: '4px', fontSize: '12px', outline: 'none' };
  const btnStyle: React.CSSProperties = {
    padding: '5px 12px', backgroundColor: '#3b82f6', color: 'white', border: 'none',
    borderRadius: '3px', cursor: 'pointer', fontSize: '12px', fontWeight: 500,
  };

  return (
    <div style={{ padding: '20px', backgroundColor: '#eff6ff', minHeight: '100vh' }}>
      {/* 헤더: 필터 / 통계 */}
      <div style={{ backgroundColor: 'white', border: '2px solid #3b82f6', borderRadius: '8px', padding: '16px', marginBottom: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <label style={labelStyle}>검색:</label>
              <input
                type="text" placeholder={futSel ? '종목코드 / 종목명 / 기초자산' : '종목코드 / 종목명'} value={search}
                onChange={e => setSearch(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') fetchList(); }}
                style={{ ...inputStyle, width: '200px' }}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <label style={labelStyle}>시장:</label>
              {(['', 'KOSPI', 'KOSDAQ', 'FUT', 'SFUT'] as const).map(v => (
                <label key={v || 'all'} style={{
                  fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer',
                  color: v === 'FUT' ? '#b45309' : v === 'SFUT' ? '#047857' : '#1e40af',
                  // 주식(현물)과 선물 사이 구분선
                  ...(v === 'FUT' ? { borderLeft: '1px solid #cbd5e1', paddingLeft: '8px' } : {}),
                }}>
                  <input type="radio" name="market" checked={market === v} onChange={() => setMarket(v)} />
                  {marketLabel[v]}
                </label>
              ))}
            </div>
            {futSel ? (
              <label style={{ fontSize: '12px', color: '#1e40af', display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
                <input type="checkbox" checked={futSpread} onChange={e => setFutSpread(e.target.checked)} />
                스프레드 포함
              </label>
            ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <label style={labelStyle}>종류:</label>
              {(['ST', 'EF', 'EN'] as const).map(g => (
                <label key={g} style={{ fontSize: '12px', color: '#1e40af', display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}>
                  <input
                    type="checkbox" checked={grpCodes[g]}
                    onChange={e => setGrpCodes(prev => ({ ...prev, [g]: e.target.checked }))}
                  />
                  {grpCodeLabels[g]}
                </label>
              ))}
            </div>
            )}
            <button onClick={fetchList} style={btnStyle}>{loading ? '조회중...' : '조회'}</button>
          </div>

          {/* 통계 카드 */}
          {futSel ? (
          <div style={{ display: 'flex', gap: '12px', fontSize: '12px', color: '#1e40af' }}>
            <div style={{ padding: '4px 10px', borderRight: '1px solid #cbd5e1' }}>
              {marketLabel[market]} <strong style={{ fontSize: '13px' }}>{futStats.total.toLocaleString()}</strong>
            </div>
            {futStats.byTp.map(([nm, n], i) => (
              <div key={nm} style={{ padding: '4px 10px', borderRight: i < futStats.byTp.length - 1 ? '1px solid #cbd5e1' : undefined }}>
                {nm} <strong style={{ fontSize: '13px' }}>{n.toLocaleString()}</strong>
              </div>
            ))}
          </div>
          ) : (
          <div style={{ display: 'flex', gap: '12px', fontSize: '12px', color: '#1e40af' }}>
            <div style={{ padding: '4px 10px', borderRight: '1px solid #cbd5e1' }}>
              전체 <strong style={{ fontSize: '13px' }}>{stats.total.toLocaleString()}</strong>
            </div>
            <div style={{ padding: '4px 10px', borderRight: '1px solid #cbd5e1' }}>
              KOSPI <strong style={{ color: '#1d4ed8', fontSize: '13px' }}>{stats.kospi.toLocaleString()}</strong>
            </div>
            <div style={{ padding: '4px 10px', borderRight: '1px solid #cbd5e1' }}>
              KOSDAQ <strong style={{ color: '#7c3aed', fontSize: '13px' }}>{stats.kosdaq.toLocaleString()}</strong>
            </div>
            <div style={{ padding: '4px 10px' }}>
              거래정지 <strong style={{ color: stats.suspended > 0 ? '#dc2626' : '#1e40af', fontSize: '13px' }}>{stats.suspended.toLocaleString()}</strong>
            </div>
          </div>
          )}
        </div>
      </div>

      {/* 그리드 카드 */}
      <div style={{ backgroundColor: 'white', border: '2px solid #3b82f6', borderRadius: '8px', padding: '12px', marginBottom: (detail || futDetail) ? '20px' : '0' }}>
        <div className="ag-theme-quartz" style={{ height: (detail || futDetail) ? 380 : 'calc(100vh - 230px)', width: '100%', fontSize: '11px' }}>
          {futSel ? (
            <AgGridReact<FutRow> {...copyMenu.gridProps}
              key="fut"
              rowData={futRows}
              columnDefs={futColumnDefs}
              defaultColDef={{ resizable: true, sortable: true, cellStyle: { fontSize: '11px' } }}
              rowSelection="single"
              onRowClicked={(e) => { if (e.data) setFutDetail(e.data); }}
              getRowId={p => p.data.prodId}
            />
          ) : (
            <AgGridReact {...copyMenu.gridProps}
              key="stk"
              rowData={filteredRows}
              columnDefs={columnDefs}
              defaultColDef={{ resizable: true, sortable: true, cellStyle: { fontSize: '11px' } }}
              rowSelection="single"
              onRowClicked={(e) => { if (e.data) fetchDetail(e.data.prodId); }}
              getRowId={p => p.data.prodId}
            />
          )}
        </div>
      </div>

      {/* 선물 상세 — 목록 행 그대로 (마스터 파일 9컬럼 + 파생 컬럼이 전부라 추가 조회 없음) */}
      {futSel && futDetail && (
        <div style={{ backgroundColor: 'white', border: '2px solid #3b82f6', borderRadius: '8px', padding: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', paddingBottom: '8px', borderBottom: '1px solid #e5e7eb' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '12px' }}>
              <h3 style={{ margin: 0, fontSize: '12px', color: '#1e3a8a', fontWeight: 700 }}>{futDetail.prodNm}</h3>
              <span style={{ fontSize: '11px', color: '#64748b', fontFamily: 'monospace' }}>{futDetail.prodId}</span>
              <span style={{
                fontSize: '11px', padding: '2px 8px', borderRadius: '10px', fontWeight: 600,
                backgroundColor: futDetail.market === '선물' ? '#fef3c7' : '#d1fae5',
                color: futDetail.market === '선물' ? '#b45309' : '#047857',
              }}>{futDetail.market}</span>
              <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '10px', backgroundColor: '#f1f5f9', color: '#1e40af', fontWeight: 500 }}>
                {futDetail.prodTpNm}
              </span>
            </div>
            <button onClick={() => setFutDetail(null)} style={{ ...btnStyle, backgroundColor: '#6b7280' }}>닫기</button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '4px 24px', fontSize: '11px' }}>
            {futDetailFields.map(f => {
              const v = futDetail[f.key];
              const display = f.key === 'monthCls' ? (monthClsLabel(v as string) || '-') : fmtVal(v);
              return (
                <div key={f.key} style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px dashed #e5e7eb', padding: '3px 0' }}>
                  <span style={{ color: '#64748b' }}>{f.label}</span>
                  <span style={{ color: '#0f172a', fontFamily: 'monospace' }}>{display}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* 상세 패널 */}
      {!futSel && detail && (
        <div style={{ backgroundColor: 'white', border: '2px solid #3b82f6', borderRadius: '8px', padding: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', paddingBottom: '8px', borderBottom: '1px solid #e5e7eb' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '12px' }}>
              <h3 style={{ margin: 0, fontSize: '12px', color: '#1e3a8a', fontWeight: 700 }}>
                {detail.prodNm}
              </h3>
              <span style={{ fontSize: '11px', color: '#64748b', fontFamily: 'monospace' }}>
                {detail.prodId}
              </span>
              <span style={{
                fontSize: '11px', padding: '2px 8px', borderRadius: '10px',
                backgroundColor: detail.market === 'KOSPI' ? '#dbeafe' : '#ede9fe',
                color: detail.market === 'KOSPI' ? '#1d4ed8' : '#7c3aed', fontWeight: 600,
              }}>{detail.market}</span>
              <span style={{
                fontSize: '11px', padding: '2px 8px', borderRadius: '10px',
                backgroundColor: '#f1f5f9', color: '#1e40af', fontWeight: 500,
              }}>{grpCodeLabels[detail.grpCode] || detail.grpCode}</span>
            </div>
            <button onClick={() => setDetail(null)} style={{ ...btnStyle, backgroundColor: '#6b7280' }}>닫기</button>
          </div>

          {detailLoading ? (
            <div style={{ fontSize: '12px', color: '#1e40af', padding: '20px', textAlign: 'center' }}>로딩 중...</div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))', gap: '16px' }}>
              {detailGroups.map(group => (
                <div key={group.title} style={{ border: '1px solid #e5e7eb', borderRadius: '6px', padding: '10px', backgroundColor: '#f8fafc' }}>
                  <div style={{ fontSize: '12px', fontWeight: 700, color: '#1e3a8a', marginBottom: '8px', paddingBottom: '4px', borderBottom: '1px solid #cbd5e1' }}>
                    {group.title}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 12px', fontSize: '11px' }}>
                    {group.fields.map(f => {
                      const v = detail[f.key];
                      const display = fmtVal(v, f.type);
                      const isYes = f.type === 'bool' && v === true;
                      const isNo = f.type === 'bool' && v === false;
                      return (
                        <React.Fragment key={f.key}>
                          <span style={{ color: '#64748b', whiteSpace: 'nowrap' }}>{f.label}</span>
                          <span style={{
                            color: isYes ? '#16a34a' : isNo ? '#94a3b8' : '#0f172a',
                            fontWeight: isYes ? 600 : 400,
                            textAlign: 'right',
                            fontFamily: f.type === 'num' ? 'monospace' : 'inherit',
                          }}>{display}</span>
                        </React.Fragment>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {copyMenu.menu}
    </div>
  );
};

export default StockMaster;
